import { useState, useEffect, useRef } from 'react';
import { getDocuments, uploadDocument, deleteDocument, getDocumentFileUrl } from '../../utils/api';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

const isImage = (mimetype) => mimetype.startsWith('image/');

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileIcon = ({ mimetype }) => {
  if (isImage(mimetype)) return <span className="doc-icon doc-icon-image">🖼</span>;
  if (mimetype === 'application/pdf') return <span className="doc-icon doc-icon-pdf">📄</span>;
  return <span className="doc-icon">📎</span>;
};

const DocumentsSection = ({ applianceId, roomId, taskId, projectId }) => {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const [tab, setTab] = useState('all');
  const fileRef = useRef();
  const { confirm } = useConfirm();
  const { showToast } = useToast();

  const queryParam = applianceId ? { applianceId }
    : roomId ? { roomId }
    : taskId ? { taskId }
    : { projectId };

  const entityParam = applianceId ? { applianceId }
    : roomId ? { roomId }
    : taskId ? { taskId }
    : { projectId };

  const load = () => getDocuments(queryParam).then(({ data }) => setDocs(data));

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('label', label);
      Object.entries(entityParam).forEach(([k, v]) => fd.append(k, v));
      await uploadDocument(fd);
      setLabel('');
      fileRef.current.value = '';
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed. Check file type (images, PDF, Word) and size (max 20 MB).');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Delete document', message: 'This file will be permanently removed.' }))) return;
    try {
      await deleteDocument(id);
      setDocs((prev) => prev.filter((d) => d._id !== id));
      showToast('Document deleted', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete document', 'error');
    }
  };

  const photos = docs.filter((d) => isImage(d.mimetype));
  const files = docs.filter((d) => !isImage(d.mimetype));
  const displayed = tab === 'photos' ? photos : tab === 'files' ? files : docs;

  return (
    <div className="detail-section">
      <div className="section-header">
        <h2>Documents & Photos</h2>
      </div>

      {/* Upload area */}
      <div className="doc-upload-area">
        <input
          className="doc-label-input"
          placeholder="Label (e.g. Receipt, Manual, Warranty)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <label className={`btn btn-primary btn-sm doc-upload-btn${uploading ? ' disabled' : ''}`}>
          {uploading ? 'Uploading…' : '+ Upload File'}
          <input
            ref={fileRef}
            type="file"
            accept="image/*,.pdf,.doc,.docx"
            style={{ display: 'none' }}
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
        <span className="doc-upload-hint">Images, PDF, Word — max 20 MB</span>
      </div>
      {error && <p className="doc-error">{error}</p>}

      {/* Tab bar */}
      {docs.length > 0 && (
        <div className="doc-tabs">
          <button className={`doc-tab${tab === 'all' ? ' active' : ''}`} onClick={() => setTab('all')}>
            All ({docs.length})
          </button>
          <button className={`doc-tab${tab === 'photos' ? ' active' : ''}`} onClick={() => setTab('photos')}>
            Photos ({photos.length})
          </button>
          <button className={`doc-tab${tab === 'files' ? ' active' : ''}`} onClick={() => setTab('files')}>
            Files ({files.length})
          </button>
        </div>
      )}

      {/* Content */}
      {displayed.length === 0 ? (
        <p className="empty-state" style={{ padding: '2rem', marginTop: '1rem' }}>
          {docs.length === 0 ? 'No documents yet.' : `No ${tab} yet.`}
        </p>
      ) : tab === 'photos' ? (
        <div className="photo-grid">
          {displayed.map((doc) => (
            <div key={doc._id} className="photo-grid-item">
              <button className="photo-grid-btn" onClick={() => setLightbox(doc)}>
                <img
                  src={getDocumentFileUrl(doc._id)}
                  alt={doc.label || doc.originalName}
                  className="photo-grid-thumb"
                />
              </button>
              <div className="photo-grid-info">
                <span className="photo-grid-label">{doc.label || doc.originalName}</span>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(doc._id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="doc-list">
          {displayed.map((doc) => (
            <div key={doc._id} className="doc-item">
              {isImage(doc.mimetype) ? (
                <button className="doc-thumb-btn" onClick={() => setLightbox(doc)}>
                  <img
                    src={getDocumentFileUrl(doc._id)}
                    alt={doc.label || doc.originalName}
                    className="doc-thumb"
                  />
                </button>
              ) : (
                <FileIcon mimetype={doc.mimetype} />
              )}
              <div className="doc-info">
                <a
                  href={getDocumentFileUrl(doc._id)}
                  target="_blank"
                  rel="noreferrer"
                  className="doc-name"
                >
                  {doc.label || doc.originalName}
                </a>
                {doc.label && <span className="doc-original">{doc.originalName}</span>}
                <span className="doc-meta">{formatSize(doc.size)} · {new Date(doc.createdAt).toLocaleDateString()}</span>
              </div>
              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(doc._id)}>Delete</button>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
            <img src={getDocumentFileUrl(lightbox._id)} alt={lightbox.label || lightbox.originalName} className="lightbox-img" />
            <p className="lightbox-caption">{lightbox.label || lightbox.originalName}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsSection;
